'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import {
  ImageViewer,
  viewerRectFromElement,
  type ViewerSourceRect,
} from '@/shared/ui';
import { ImageComparisonSlider } from '@/shared/ui/ImageComparisonSlider';
import { Badge } from '@/shared/ui/shadcn/badge';
import { useTouchSwipe } from '@/shared/hooks/use-touch-swipe';
import {
  computeImageContainerStyle,
  clampAspectRatio,
  type ImageSizingConstraints,
} from '@/shared/hooks';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('AngleImageDisplay');

interface AngleImageDisplayProps {
  sourceUrl?: string;
  renderUrl?: string;
  alt: string;
  containerClassName?: string;
  isUpscaled?: boolean;
  /** Whether a render is actively in progress (applies grayscale to image) */
  isRendering?: boolean;
  onDraggingChange?: (isDragging: boolean) => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  /** Called when user clicks on the image (to open viewer externally) */
  onImageClick?: () => void;
  /** External control for viewer open state */
  isViewerOpenExternal?: boolean;
  /** Callback when viewer is closed (for external control) */
  onViewerClose?: () => void;
  /** Per-layout sizing constraints (Apple-parity caps/floors/lift). */
  sizing: ImageSizingConstraints;
  /** External programmatic slider position (0..100, LTR semantic — 0 = before
   *  fully visible, 100 = after fully visible). Drives the compare slider
   *  to a target position when render-form mode changes. */
  sliderProgrammaticPosition?: number;
  /** Angle aspect ratio (width / height) known from the API before the image
   *  loads. Seeds the container shape on the first render so it doesn't reflow
   *  from the 16:9 default once the browser decodes the image. */
  initialAspectRatio?: number;
}

export function AngleImageDisplay({
  sourceUrl,
  renderUrl,
  alt,
  containerClassName = '',
  isUpscaled = false,
  isRendering = false,
  onDraggingChange,
  onSwipeLeft,
  onSwipeRight,
  onImageClick,
  isViewerOpenExternal,
  onViewerClose,
  sizing,
  sliderProgrammaticPosition,
  initialAspectRatio,
}: AngleImageDisplayProps) {
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  // Available column width — drives the single-column landscape height-lift.
  // Measured on a full-width wrapper so it is known even when the container
  // itself is height-bound (portrait, width:auto).
  const [cardWidth, setCardWidth] = useState(0);
  const measureRef = useRef<HTMLDivElement>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  // On-screen rect of the image frame at tap time — drives the viewer hero morph.
  const [sourceRect, setSourceRect] = useState<ViewerSourceRect | null>(null);
  // Tracked so the fullscreen viewer can open at the same handle position the user
  // is looking at on the card (mode-driven or hand-dragged) — Apple parity.
  const [sliderPosition, setSliderPosition] = useState(0);
  const [isDraggingSlider, setIsDraggingSlider] = useState(false);
  const [wasRecentlyDragging, setWasRecentlyDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // DEBUG: Log props on mount and update
  // console.log(`[AngleImageDisplay ${alt}] Props:`, {
  //   sourceUrl,
  //   renderUrl,
  //   hasSource: !!sourceUrl,
  //   hasRender: !!renderUrl,
  // });

  // Get dimensions from the source image
  useEffect(() => {
    if (!sourceUrl) {
      // console.log(
      //   `[AngleImageDisplay ${alt}] No sourceUrl, skipping dimensions`
      // );
      return;
    }

    logger.debug(`Loading dimensions from (${alt}):`, sourceUrl);

    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 2000; // 2 seconds
    let timeoutId: NodeJS.Timeout;
    let currentImg: HTMLImageElement | null = null;

    const tryLoadImage = () => {
      const img = new window.Image();
      currentImg = img;

      img.onload = () => {
        setDimensions({ width: img.width, height: img.height });
      };

      img.onerror = (_error) => {
        if (retryCount < maxRetries) {
          // Expected: CDN propagation delay, just log and retry
          retryCount++;
          // console.log(
          //   `[AngleImageDisplay ${alt}] Image not ready, retrying (${retryCount}/${maxRetries}) in ${retryDelay}ms...`
          // );
          timeoutId = setTimeout(tryLoadImage, retryDelay);
        } else {
          // Unexpected: Failed after all retries
          // console.error(
          //   `[AngleImageDisplay ${alt}] Failed to load after ${maxRetries} retries:`,
          //   error
          // );
          setDimensions(null);
        }
      };

      // On retries, add cache-busting param to bypass browser-cached 404
      // (race condition: WebSocket reload can trigger before S3 upload completes)
      img.src =
        retryCount > 0
          ? `${sourceUrl}${sourceUrl.includes('?') ? '&' : '?'}_r=${Date.now()}`
          : sourceUrl;
    };

    tryLoadImage();

    return () => {
      if (currentImg) {
        currentImg.onload = null;
        currentImg.onerror = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [sourceUrl, alt]);

  // Preload render image to prevent flashing
  useEffect(() => {
    if (renderUrl) {
      const img = new window.Image();
      img.src = renderUrl;
    }
  }, [renderUrl]);

  // Handle dragging state change
  const handleDraggingChange = (dragging: boolean) => {
    setIsDraggingSlider(dragging);
    onDraggingChange?.(dragging);

    // When dragging ends, add a short delay before re-enabling swipe
    // This prevents the touchend event from being interpreted as a swipe
    if (!dragging) {
      setWasRecentlyDragging(true);

      // Clear any existing timeout
      if (draggingTimeoutRef.current) {
        clearTimeout(draggingTimeoutRef.current);
      }

      // Re-enable swipe after 200ms
      draggingTimeoutRef.current = setTimeout(() => {
        setWasRecentlyDragging(false);
      }, 200);
    } else {
      // If starting to drag, immediately disable swipe
      setWasRecentlyDragging(false);
      if (draggingTimeoutRef.current) {
        clearTimeout(draggingTimeoutRef.current);
        draggingTimeoutRef.current = null;
      }
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (draggingTimeoutRef.current) {
        clearTimeout(draggingTimeoutRef.current);
      }
    };
  }, []);

  // Add swipe support for navigating concepts (only on main angle)
  // Swipe is disabled when dragging the comparison slider or shortly after
  useTouchSwipe({
    onSwipeLeft,
    onSwipeRight,
    enabled:
      !isDraggingSlider &&
      !wasRecentlyDragging &&
      (!!onSwipeLeft || !!onSwipeRight),
    element: containerRef.current,
  });

  // Measure the available column width on a full-width wrapper (known even when
  // the sized container is height-bound). Feeds the landscape height-lift.
  useEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const measure = () => setCardWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Aspect ratio (clamped 3:4..16:9). Prefer the measured size; before it loads,
  // fall back to the API-known angle aspect so the container is correctly shaped
  // on the first render (no reflow). Default 16:9 when neither is available.
  const aspectRatio = clampAspectRatio(
    dimensions
      ? dimensions.width / dimensions.height
      : (initialAspectRatio ?? null)
  );
  const hasRender = !!renderUrl;

  // Container style — Apple-parity caps/floors + single-column landscape lift.
  const { style: containerStyle } = computeImageContainerStyle(
    aspectRatio,
    sizing,
    cardWidth
  );

  // Build viewer images: render (with before/after comparison) or just source.
  // When showing only the original angle (no render), mark it so the viewer renders
  // it grayscale, matching the small card treatment.
  const viewerImages: {
    src: string;
    alt?: string;
    beforeSrc?: string;
    comparisonPosition?: number;
    isOriginalAngle?: boolean;
  }[] = [];
  if (hasRender && renderUrl) {
    viewerImages.push({
      src: renderUrl,
      alt: `Rendered ${alt}`,
      beforeSrc: sourceUrl, // enables comparison mode in viewer
      comparisonPosition: sliderPosition, // open fullscreen at the card's handle position
    });
  } else if (sourceUrl) {
    viewerImages.push({ src: sourceUrl, alt, isOriginalAngle: true });
  }

  return (
    <>
      <div
        ref={measureRef}
        className="w-full flex flex-col items-center justify-center"
      >
      <div
        ref={containerRef}
        data-image-frame
        // `rounded-lg overflow-hidden` clips the image itself (the
        // comparison slider, the `<Image>` element, the 4K badge) to
        // rounded corners. The outer wrapper in MainAngleDisplay is
        // `rounded-2xl`; nesting a slightly tighter radius on the image
        // matches Apple/Android — the image reads as a framed panel
        // inside the settings-card surface, not a square photo behind a
        // rounded mask.
        className={`relative rounded-lg overflow-hidden ${containerClassName} transition-[filter] duration-[600ms] ${isRendering ? 'saturate-[0.25] brightness-95' : !hasRender ? 'saturate-[0.25] brightness-95 opacity-60' : ''} ${onImageClick ? 'cursor-pointer' : ''}`}
        style={containerStyle}
        onClick={(e) => {
          // Don't open viewer if user is interacting with the comparison slider
          if (isDraggingSlider || wasRecentlyDragging) return;
          // Don't open viewer if clicking on interactive children (badges, buttons)
          if ((e.target as HTMLElement).closest('button, [role="button"], a')) return;
          // Snapshot the frame rect so the viewer morphs out of the tapped card.
          setSourceRect(viewerRectFromElement(containerRef.current));
          onImageClick?.();
        }}
      >
        {/* Use comparison slider when we have both images */}
        {sourceUrl && hasRender && renderUrl ? (
          <div className="w-full h-full absolute inset-0">
            <ImageComparisonSlider
              // Key on the SOURCE (angle) only — the slider tracks render-URL
              // changes internally (resets just the after side + double-buffers
              // the outgoing render). Keying on the render URL too forced a full
              // remount that flashed the loading overlay on every render swap.
              key={sourceUrl}
              beforeImage={sourceUrl} // Source/angle on the left
              afterImage={renderUrl} // Render on the right
              beforeAlt={alt}
              afterAlt={`Rendered ${alt}`}
              initialPosition={0} // Start at left position to show render fully
              aspectRatio={aspectRatio}
              // Cover, not contain: the container is sized to the ANGLE aspect,
              // so the angle fills it exactly and a mismatched render is
              // center-cropped to fill rather than letterboxed. Unified rule
              // across all 3 platforms — see Apple/Android RenderModeToggle.
              objectFit="cover"
              onPositionChange={setSliderPosition}
              onDraggingChange={handleDraggingChange}
              className="w-full h-full"
              programmaticPosition={sliderProgrammaticPosition}
            />
          </div>
        ) : sourceUrl ? (
          // Show only source image if no render
          <Image
            key={`${sourceUrl}-${dimensions?.width || 'loading'}`} // Force reload when dimensions load
            src={sourceUrl}
            alt={alt}
            fill
            sizes="(max-width: 768px) 100vw, 768px"
            // Container is the angle aspect → cover shows the angle full size.
            className="object-cover"
            priority
          />
        ) : null}

        {/* 4K badge for upscaled renders */}
        {isUpscaled && renderUrl && (
          <div className="absolute top-2 left-2 z-10">
            <Badge
              variant="outline"
              className="bg-transparent text-primary border-primary text-chip px-2 py-0.5 backdrop-blur-sm"
            >
              4K
            </Badge>
          </div>
        )}
      </div>
      </div>

      {/* Image Viewer Modal */}
      {viewerImages.length > 0 && (
        <ImageViewer
          images={viewerImages}
          sourceRect={sourceRect}
          isOpen={
            isViewerOpenExternal !== undefined
              ? isViewerOpenExternal
              : isViewerOpen
          }
          onClose={() => {
            if (onViewerClose) {
              onViewerClose();
            } else {
              setIsViewerOpen(false);
            }
          }}
        />
      )}
    </>
  );
}
